// src/components/TermsOfService.js
import React, { useState } from "react";

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    icon: "✦",
    content: `By accessing or using the services offered by Nairobi's Ultimate Experience City Tours & Adventure ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services. These terms apply to all visitors, users, and clients who access or use our tours, website, or any related services.`,
  },
  {
    id: "services",
    title: "Our Services",
    icon: "✦",
    content: `Nairobi's Ultimate Experience provides curated city tours, cultural experiences, excursions, and adventure activities in and around Nairobi, Kenya. Our services include but are not limited to: Nairobi Downtown Walking Tours, Historical & Cultural Tours, Day Excursions, and Community (Slum) Tours. All tours are subject to availability and may be modified or cancelled at our discretion due to safety, weather, or operational reasons.`,
  },
  {
    id: "booking",
    title: "Bookings & Payments",
    icon: "✦",
    content: `All bookings are subject to availability and confirmation by our team. Payment is required to secure your reservation. We accept payments via M-Pesa, bank transfer, and other methods as communicated at the time of booking. Prices are quoted in Kenyan Shillings (KES) unless otherwise stated. We reserve the right to adjust pricing without prior notice, though confirmed bookings will honour the quoted price.`,
  },
  {
    id: "cancellation",
    title: "Cancellation & Refund Policy",
    icon: "✦",
    content: `Cancellations made 48 hours or more before the tour date are eligible for a full refund or rescheduling at no additional cost. Cancellations made within 24–48 hours of the tour will incur a 50% cancellation fee. No refunds will be issued for cancellations made less than 24 hours before the tour or for no-shows. In the event that we cancel a tour due to circumstances within our control, a full refund or alternative date will be offered. Cancellations due to weather, civil unrest, or other force majeure events will be handled on a case-by-case basis.`,
  },
  {
    id: "conduct",
    title: "Client Conduct",
    icon: "✦",
    content: `Clients are expected to behave respectfully toward guides, fellow travellers, and local communities at all times. We reserve the right to remove any client from a tour without refund if their behaviour is deemed unsafe, disruptive, or disrespectful to others. Photography of community members should only be done with explicit consent. We promote responsible and ethical tourism and expect all clients to uphold these values.`,
  },
  {
    id: "liability",
    title: "Liability & Risk",
    icon: "✦",
    content: `Participation in our tours involves inherent risks. By booking with us, you acknowledge and accept these risks. Nairobi's Ultimate Experience shall not be held liable for any personal injury, loss, damage, or expense arising from participation in our tours, unless caused by our direct negligence. We strongly recommend that all clients obtain comprehensive travel insurance prior to their visit to Nairobi.`,
  },
  {
    id: "ip",
    title: "Intellectual Property",
    icon: "✦",
    content: `All content on our website and marketing materials — including text, photographs, logos, and branding — is the property of Nairobi's Ultimate Experience and is protected by applicable intellectual property laws. You may not reproduce, distribute, or use our content without express written permission.`,
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    icon: "✦",
    content: `We reserve the right to update or modify these Terms of Service at any time. Changes will be posted on our website and take effect immediately upon publication. Continued use of our services after any changes constitutes your acceptance of the new terms. We encourage you to review these terms periodically.`,
  },
  {
    id: "governing",
    title: "Governing Law",
    icon: "✦",
    content: `These Terms of Service are governed by and construed in accordance with the laws of the Republic of Kenya. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Nairobi, Kenya.`,
  },
  {
    id: "contact",
    title: "Contact Us",
    icon: "✦",
    content: `If you have any questions about these Terms of Service, please reach out to us:\n\nEmail: nairobisultimateexperience@gmail.com\nPhone: +254 105 057 005\nLocation: Nairobi, Kenya`,
  },
];

function TermsOfService() {
  const [activeSection, setActiveSection] = useState(null);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
          --charcoal: #141414;
          --card-bg: rgba(255,255,255,0.03);
        }

        .tos-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* Hero */
        .tos-hero {
          background: linear-gradient(160deg, #0f0d07 0%, #1c1600 60%, #0A0A0A 100%);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem 3.5rem;
          text-align: center;
        }
        .tos-eyebrow {
          font-size: 0.68rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.8rem;
        }
        .tos-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.6rem;
        }
        .tos-divider {
          width: 50px;
          height: 2px;
          background: var(--gold);
          margin: 1.2rem auto;
        }
        .tos-updated {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.35);
          letter-spacing: 1px;
        }

        /* Layout */
        .tos-body {
          max-width: 860px;
          margin: 0 auto;
          padding: 4rem 1.5rem 6rem;
        }

        /* TOC */
        .tos-toc {
          background: rgba(201,168,76,0.06);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 8px;
          padding: 2rem 2rem 1.5rem;
          margin-bottom: 3.5rem;
        }
        .tos-toc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--gold-light);
          margin-bottom: 1rem;
          letter-spacing: 1px;
        }
        .tos-toc ol {
          padding-left: 1.2rem;
          margin: 0;
          columns: 2;
          column-gap: 2rem;
        }
        .tos-toc li {
          margin-bottom: 0.5rem;
        }
        .tos-toc a {
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          font-size: 0.83rem;
          transition: color 0.2s;
        }
        .tos-toc a:hover { color: var(--gold); }

        /* Sections */
        .tos-section {
          border-bottom: 1px solid rgba(255,255,255,0.06);
          padding: 2.5rem 0;
          cursor: default;
        }
        .tos-section:last-child { border-bottom: none; }

        .tos-section-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.2rem;
        }
        .tos-section-icon {
          color: var(--gold);
          font-size: 0.6rem;
          margin-top: 0.55rem;
          flex-shrink: 0;
          letter-spacing: -1px;
        }
        .tos-section-num {
          font-size: 0.65rem;
          letter-spacing: 2px;
          color: rgba(201,168,76,0.5);
          font-weight: 500;
          text-transform: uppercase;
          display: block;
          margin-bottom: 0.2rem;
        }
        .tos-section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.3rem, 3vw, 1.75rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.15;
          margin: 0;
        }
        .tos-section-text {
          font-size: 0.93rem;
          font-weight: 300;
          line-height: 1.95;
          color: rgba(255,255,255,0.65);
          white-space: pre-line;
          padding-left: 1.6rem;
        }

        /* Footer note */
        .tos-footer-note {
          background: rgba(201,168,76,0.06);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          margin-top: 4rem;
        }
        .tos-footer-note p {
          font-size: 0.88rem;
          color: rgba(255,255,255,0.6);
          margin: 0;
          line-height: 1.8;
        }
        .tos-footer-note a {
          color: var(--gold);
          text-decoration: none;
        }
        .tos-footer-note a:hover { color: var(--gold-light); }

        @media (max-width: 600px) {
          .tos-toc ol { columns: 1; }
        }
      `}</style>

      <div className="tos-root">
        {/* Hero */}
        <div className="tos-hero">
          <p className="tos-eyebrow">Legal · Nairobi's Ultimate Experience</p>
          <h1 className="tos-title">Terms of Service</h1>
          <div className="tos-divider" />
          <p className="tos-updated">Last updated: May 2025</p>
        </div>

        <div className="tos-body">
          {/* TOC */}
          <div className="tos-toc">
            <div className="tos-toc-title">Contents</div>
            <ol>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {sections.map((s, i) => (
            <div key={s.id} id={s.id} className="tos-section">
              <div className="tos-section-header">
                <span className="tos-section-icon">{s.icon}</span>
                <div>
                  <span className="tos-section-num">Section {String(i + 1).padStart(2, "0")}</span>
                  <h2 className="tos-section-title">{s.title}</h2>
                </div>
              </div>
              <p className="tos-section-text">{s.content}</p>
            </div>
          ))}

          {/* Footer note */}
          <div className="tos-footer-note">
            <p>
              Questions about these terms?{" "}
              <a href="mailto:nairobisultimateexperience@gmail.com">
                nairobisultimateexperience@gmail.com
              </a>
              <br />
              <a href="/privacy-policy">Read our Privacy Policy →</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default TermsOfService;
